import { useContext } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { AuthContext } from '@core/providers/AuthProvider';
import useIsAuthenticated from './useIsAuthenticated';

export interface AuthReady {
  /** MSAL has finished startup / handleRedirectPromise / any interaction. */
  isAuthSettled: boolean;
  /** Settled AND an account exists — safe to send `Authorization`. */
  isUserReady: boolean;
  /** Settled AND definitively no account — the anonymous shared-study case. */
  isAnonymous: boolean;
  /** isUserReady AND the product token is in hand — safe to send `Organization-Authorization`. */
  isOrgReady: boolean;
  organizationId?: string;
}

/**
 * Single answer to "may I issue an authenticated request yet?".
 *
 * Auth-scoped queries used to fire at mount, before any token existed, 401 and
 * then get papered over by a later refetch. Gating their `enabled` on this hook
 * makes the first attempt the correct one.
 *
 * `inProgress === None` is what makes this correct on a warm session:
 * msal-react's `useIsAuthenticated()` flips true as soon as accounts are
 * rehydrated from cache, which happens well before `handleRedirectPromise`
 * resolves and the API token is actually obtainable.
 *
 * Note the AuthContext default has `isAcquiring: false`, so a consumer rendered
 * outside AuthContextProvider reports `isOrgReady === false` — fail-closed.
 * Because of that, never gate an anonymous-allowed query on `isOrgReady`.
 */
const useAuthReady = (): AuthReady => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { organization, isAcquiring } = useContext(AuthContext);

  const isAuthSettled = inProgress === InteractionStatus.None;
  const isUserReady = isAuthSettled && isAuthenticated;
  const organizationId = organization?.organizationId || undefined;

  return {
    isAuthSettled,
    isUserReady,
    isAnonymous: isAuthSettled && !isAuthenticated,
    isOrgReady: isUserReady && !!organizationId && !isAcquiring,
    organizationId,
  };
};

export default useAuthReady;
